import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import FormField, { Input } from './ui/FormField';
import StatusBanner from './ui/StatusBanner';
import { EnvelopeIcon, LockClosedIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { apiErrorMessage } from '../utils/apiError';

interface LoginProps {
  onSwitchToRegister?: () => void;
}

const Login: React.FC<LoginProps> = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectQuery = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Login failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Visual (Nitika's "Image for Main Page" + sage scrim) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Photo */}
        <img
          src="/assets/login-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Scrim — tuned so the lower ~60% (where the logo + all copy sit) is
            reliably dark for legible white elements, while the top third stays
            clear so the photo breathes. Standard hero gradient, not a flat box. */}
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(56,57,52,0.96)_0%,rgba(56,57,52,0.86)_45%,rgba(56,57,52,0.55)_62%,transparent_85%)]" />

        {/* One cohesive brand block, bottom-anchored (logo → message → features).
            No top-pinned logo = no dead zone in the middle. */}
        <div className="relative z-10 mt-auto flex flex-col px-12 lg:px-20 pb-20">
          {/* Logo, no card — knocked out to white. Legibility comes from a soft
              local shadow (not a heavy scrim), so the photo still breathes. */}
          <a href="https://www.clinicstudios.ca" aria-label="Clinic Studios — back to main site" className="inline-block w-fit mb-12 hover:opacity-90 transition-opacity">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-16 w-auto object-contain [filter:brightness(0)_invert(1)_drop-shadow(0_2px_12px_rgba(0,0,0,0.6))]"
            />
          </a>

          {/* Headline matches Nitika's PDF page 2: uppercase display treatment,
              "A Space for Healing and Growth". Fluid size so the two forced
              lines never rewrap at panel widths. */}
          <h1 className="font-display text-[clamp(2rem,3.2vw,2.875rem)] font-bold text-white uppercase tracking-[0.08em] leading-[1.15] mb-7 lg:whitespace-nowrap [text-shadow:0_2px_12px_rgba(0,0,0,0.45)]">
            A Space for
            <br />
            Healing and Growth
          </h1>
          <div className="h-1 w-16 rounded-full bg-clinic-gold mb-8" aria-hidden="true" />

          <p className="text-lg text-white/90 max-w-md mb-8 leading-relaxed [text-shadow:0_1px_8px_rgba(0,0,0,0.4)]">
            Connect with your clinician, manage appointments, and take the next step on your wellness journey.
          </p>

          {/* Learn More — gateway to the public client page (per Nitika: a button
              directly under the tagline). */}
          <Link
            to="/clients"
            className="group inline-flex items-center w-fit px-6 py-2.5 mb-10 rounded-full border border-clinic-gold text-clinic-gold font-medium hover:bg-clinic-gold hover:text-clinic-dark transition-colors"
          >
            Learn More
            <ArrowRightIcon className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>

          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              'Easy online booking',
              'Secure & confidential',
              'Flexible scheduling',
            ].map((feature, index) => (
              <div key={index} className="flex items-center text-white/85">
                <div className="w-2 h-2 bg-clinic-gold rounded-full mr-3 flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo — also the route back to the marketing site (logo = home) */}
          <div className="lg:hidden mb-8 text-center">
            <a href="https://www.clinicstudios.ca" aria-label="Clinic Studios — main site" className="inline-block">
              <img
                src="/logos/logo-primary.png"
                alt="Clinic Studios"
                className="h-12 w-auto mx-auto mb-4"
              />
            </a>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold text-clinic-dark">
              Sign In to Access Your Account
            </h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <StatusBanner
                variant="error"
                title="Unable to sign in"
                message={error}
                dismissible
                onDismiss={() => setError('')}
              />
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="Email address" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftIcon={<EnvelopeIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <FormField label="Password" htmlFor="password" required>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-clinic-primary hover:text-clinic-primary-dark transition-colors"
              >
                Forgot Your Password?
              </Link>
            </div>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isLoading}
              rightIcon={!isLoading && <ArrowRightIcon className="h-5 w-5" />}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>

            {/* Per Nitika's PDF: "Create an Account" is a full button, and the
                directory action is "a button that looks just like 'Create an
                Account'" — two identical secondary buttons, stacked. */}
            <Button
              variant="secondary"
              fullWidth
              as={Link}
              to={`/register${redirectQuery}`}
            >
              Create an Account
            </Button>

            <Button
              variant="secondary"
              fullWidth
              as={Link}
              to="/clinicians"
            >
              Browse Our Clinician Directory
            </Button>
          </form>

          {/* Minimal legal footer — the standard for an auth screen. */}
          <div className="mt-10 pt-6 border-t border-clinic-primary-lighter text-center text-sm text-clinic-muted">
            <Link to="/privacy" className="hover:text-clinic-primary transition-colors">Privacy Policy</Link>
            <span className="mx-3 text-clinic-primary-lighter">·</span>
            <Link to="/terms" className="hover:text-clinic-primary transition-colors">Terms of Service</Link>
            <span className="mx-3 text-clinic-primary-lighter">·</span>
            <a href="https://www.clinicstudios.ca" className="hover:text-clinic-primary transition-colors">clinicstudios.ca&nbsp;↗</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
